import { useState } from "react"

type ThemeImportModalProps = {
  onImport: (values: Record<string, string>) => void
  onClose: () => void
}

export const ThemeImportModal = ({ onImport, onClose }: ThemeImportModalProps) => {
  const [cssInput, setCssInput] = useState("")
  const [error, setError] = useState<string | undefined>(undefined)

  const parseCSSVariables = (cssText: string): Record<string, string> | undefined => {
    const variables: Record<string, string> = {}

    // Match CSS variable declarations like: --variable-name: value;
    const regex = /--([a-zA-Z0-9-]+):\s*([^;]+);/g
    let match
    let foundAny = false

    while ((match = regex.exec(cssText)) !== null) {
      const [, name, value] = match
      if (name && value) {
        variables[name] = value.trim()
        foundAny = true
      }
    }

    return foundAny ? variables : undefined
  }

  const handleImport = () => {
    setError(undefined)

    const parsed = parseCSSVariables(cssInput)

    if (!parsed) {
      setError("No valid CSS variables found. Make sure to paste CSS in the format: --variable-name: value;")
      return
    }

    onImport(parsed)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-p400 w-full max-w-2xl rounded-lg p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-t100 text-lg font-semibold">Import Theme CSS</h3>
          <button onClick={onClose} className="hover:text-t100 text-t300">
            ✕
          </button>
        </div>

        <p className="text-t200 mb-4 text-sm">
          Paste CSS variables to preview the theme in your UI. This will update the theme values temporarily.
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 rounded border border-alert-700 bg-alert-900 p-3 text-sm text-alert-500">{error}</div>
        )}

        {/* CSS Input */}
        <div className="mb-4">
          <label className="text-t200 mb-2 block text-sm">Paste CSS Variables</label>
          <textarea
            value={cssInput}
            onChange={(e) => setCssInput(e.target.value)}
            placeholder={`:root {
  --neutral: 223 41% 11%;
  --primary: 220 86% 28%;
  --secondary: 175 100% 14%;
  ...
}`}
            className="text-t200 focus:border-p400-light bg-p400 border-p400-light h-64 w-full rounded border p-4 font-mono text-sm outline-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-t100 bg-p400 hover:bg-p400-light rounded px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!cssInput.trim()}
            className="text-t100 rounded bg-primary-700 px-4 py-2 text-sm transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Import & Apply
          </button>
        </div>
      </div>
    </div>
  )
}
